/**
 * One currency pane of the Profit Over Time chart (CHO-752): profit per
 * property per month (revenue − costs), negative months rendered against a
 * visible zero baseline. Includes the mortgage-principal toggle and the
 * always-visible treatment caption. Prop-driven, no fetching.
 */
import { useId } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CombinedChartRow } from '../../lib/dashboard-costs';
import { formatAxisTick, formatCurrency } from '../../lib/format';

export interface ProfitChartProps {
  currency: string;
  rows: CombinedChartRow[];
  /** Property line names present in this currency pane, in display order. */
  properties: string[];
  /** Line color per property name. */
  colors: Record<string, string>;
  /** Include mortgage/principal costs (default cash-flow reading: true). */
  includePrincipal: boolean;
  onIncludePrincipalChange: (include: boolean) => void;
  height?: number;
}

export function ProfitChart({
  currency,
  rows,
  properties,
  colors,
  includePrincipal,
  onIncludePrincipalChange,
  height = 280,
}: ProfitChartProps) {
  const toggleId = useId();

  if (properties.length === 0) {
    return <p className="text-sm text-gray-500">No {currency} properties in range.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-400">
          Profit = revenue − all costs{' '}
          {includePrincipal
            ? 'incl. mortgage principal (cash-flow view)'
            : 'excl. mortgage principal'}
        </p>
        <label htmlFor={toggleId} className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input
            id={toggleId}
            type="checkbox"
            checked={includePrincipal}
            onChange={(e) => onIncludePrincipalChange(e.target.checked)}
            className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
          />
          Include mortgage principal
        </label>
      </div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis
              tick={{ fontSize: 11 }}
              domain={['auto', 'auto']}
              tickFormatter={(v: number) => formatAxisTick(v, currency)}
              width={80}
            />
            <Tooltip
              formatter={(value, name) => [
                formatCurrency(Number(value), currency),
                name,
              ]}
            />
            <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 4" />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {properties.map((name) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={colors[name] ?? '#94a3b8'}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
